import { DateTime, Interval } from 'luxon';
import { sprintf, languageCode } from '~/javascripts/lib/locale';

/**
 * @example
 * dateFromNow(31) -> "2020-11-06"
 * @param {number} daysFromNow
 * @returns {string} */
export function dateFromNow(daysFromNow) {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString().substring(0, 10);
}

export function percentageUsed(startDate, endDate) {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  const totalDiff = start.diff(end);
  const elapsedDiff = start.diffNow();
  const now = DateTime.local();

  if (start > now) {
    return 0;
  }

  if (end < now) {
    return 100;
  }

  return ((elapsedDiff / totalDiff) * 100).toFixed(1);
}

export function percentageLeft(startDate, endDate) {
  return Math.max(0, 100 - percentageUsed(startDate, endDate)).toFixed(1);
}

/**
 * Formats date to a human-readable format with Luxon.
 * @param {String|luxon.DateTime} date Date to format
 * @param {luxon.DateTime} [format=Luxon.DateTime.DATE_MED]
 * format preset from {@link https://moment.github.io/luxon/docs/class/src/datetime.js~DateTime.html#instance-method-toLocaleString|Luxon}
 * @returns {String} Formatted date
 */
export function toLocaleDateString(date, format = DateTime.DATE_MED) {
  const locale = languageCode();
  // eslint-disable-next-line no-param-reassign
  format = { ...format, locale };

  if (DateTime.isDateTime(date)) {
    return date.toLocaleString(format);
  }

  return DateTime.fromISO(date).toLocaleString(format);
}

/* eslint-disable-next-line sonarjs/no-identical-functions */
export function toLocaleDateTimeString(date, format = DateTime.DATETIME_MED) {
  const locale = languageCode();
  // eslint-disable-next-line no-param-reassign
  format = { ...format, locale };

  if (DateTime.isDateTime(date)) {
    return date.toLocaleString(format);
  }

  return DateTime.fromISO(date).toLocaleString(format);
}

/**
 * Determines if date is in the future, using UTC time.
 *
 * @param {String|luxon.DateTime} date Date to evaluate
 * @returns {boolean} Whether date is in the future
 */
export function isFutureDate(date) {
  if (DateTime.isDateTime(date)) {
    return date.diffNow() > 0;
  }

  return DateTime.fromISO(date).diffNow() > 0;
}

export function prorationCopy({ startDate, endDate, namespace, productName }) {
  const percentage = percentageUsed(startDate, endDate);

  return sprintf(
    `Credit for %{percentage}% of %{namespace}'s subscription term (your %{productName} purchase will coterminate and renew with your main subscription, which expires on %{formattedEndDate})`,
    {
      percentage,
      productName,
      namespace,
      formattedEndDate: toLocaleDateString(endDate),
    },
  );
}

/**
 * Formats two ISO dates into a date range
 *
 * @param {String} start
 * @param {String} end
 * @returns Formatted range, i.e. 1 Jul - 31 Jul, 2025
 */
export function dateRangeFormatted(start, end) {
  const startDate = DateTime.fromISO(start);
  const endDate = DateTime.fromISO(end);

  const sameYear = startDate.year === endDate.year;

  if (sameYear) {
    return `${startDate.toFormat('d MMM')} - ${endDate.toFormat('d MMM, yyyy')}`;
  }

  return `${startDate.toFormat('d MMM, yyyy')} - ${endDate.toFormat('d MMM, yyyy')}`;
}

/**
 * Get array of dates (ISO Date) between two dates.
 *
 * @param {String} startDate
 * @param {String} endDate
 * @returns {Array<String>}
 */
export function getDatesInRange(startDate, endDate) {
  const start = DateTime.fromISO(startDate, { zone: 'utc' });
  const end = DateTime.fromISO(endDate, { zone: 'utc' });

  return Interval.fromDateTimes(start.startOf('day'), end.endOf('day'))
    .splitBy({ day: 1 })
    .map((date) => date.start.toISODate());
}
